import ObjectCopyService from 'external-services/object-copy-service';
import DateUtilsService from 'external-services/date-utils-service';
import {
  ITableItem,
  BookingMethods,
  TimeStatus,
  IBlockout,
  ITableItemAbstract,
  ServiceEventType,
  IBooking,
  BookingType,
  RequiredTable,
  SECTION_ANY_ID,
} from 'shared-types/SharedTypes';
import {
  IScheduleTime,
  ILayoutMinimal,
  IDiarySchedule,
  bookingStatusType,
} from 'shared-types/index';
import LayoutsService from 'external-services/layouts-service/layouts.service';
import {
  IBookingsByTable,
  IScheduleSubset,
  IBookingInfoAtTime,
  IBookingSubset,
  ILayoutSubset,
  ITableAvailableLookUp,
  IServiceItemSubset,
  IServiceItemSubsetMinimal,
  IBookingSubsetForMessage,
} from 'external-services/booking-availability-service/types';
import AdminService from 'external-services/diary-admin-service/admin.service';
import { StatusService } from 'external-services/status-service';
import { uniq } from 'lodash';
import moment from 'moment';
import { cloneDeep } from 'lodash-es';

const NS = 'BookingAvailabilityService';

// use BookingAvailabilityService directly for static function references
export class BookingAvailabilityService {
  /**
   * Due to complexity of these public functions, private functions have been exposed for unit testing as well
   */
  static __getPrivateFunctionsForUnitTests() {
    return {
      getTotalTimes: this.getTotalTimes.bind(this),
      getBookingTimes: this.getBookingTimes.bind(this),
      getTableJoinsFromLayouts: this.getTableJoinsFromLayouts.bind(this),
      getMaxCoversAtTime: this.getMaxCoversAtTime.bind(this),
      formatDate: this.formatDate.bind(this),
      getBlockedSectionAndTableIdsAtTime: this.getBlockedSectionAndTableIdsAtTime.bind(this),
      getBookingIntervalFromService: this.getBookingIntervalFromService.bind(this),
      getBookingInfoAtTime: this.getBookingInfoAtTime.bind(this),
      getBookingSlotsByServiceSubset: this.getBookingSlotsByServiceSubset.bind(this),
      getBookingGap: this.getBookingGap.bind(this),
      findBookingSlotsForSharedTable: this.findBookingSlotsForSharedTables.bind(this),
      getTableStatus: this.getTableStatus.bind(this),
      getMessages: this.getMessages.bind(this),
      getRequestedTime: this.getRequestedTime.bind(this),
      setValidTable: this.setValidTable.bind(this),
      addObstructingBookingMessageToTable: this.addObstructingBookingMessageToTable.bind(this),
    };
  }

  /**
   * useful function for debugging start/end times in this service, which are stiored as accumulated minutes
   * @param startHour - the hour of the day that the minutes start at. For example, 06:00 would just be 6
   * @param startMin - for example requestedStartMin or requestedEndMin
   * @return formatted 24 hour time. For example 13:30
   */
  private static getTimeForDebugging(startHour: number, startMin: number): string {
    const totalHours = Math.floor(startMin / 60);
    const remainingMins = Math.round((startMin / 60 - totalHours) * 60);
    return `${startHour + totalHours}:${remainingMins}`;
  }

  /**
   * Returns a list of available booking times
   * @param  {Object}  schedule           The schedule you are booking in
   * @param  {Object}  serviceStartMin    The service you are booking for
   * @param  {Number}  requestDuration    How long you wish to book for
   * @param  {Number}  requestedPeople    The amount of people you wish to book for
   * @param  {Object}  bookingsByTable    A object of bookings by table see getBookingsByTable
   * @param  {String} preferredSectionId  The section id the guest would like to sit it
   * @param  {String} requiredTableId     The id of the table the guest would like to sit at
   * @param  {Booelan} allowPassTimes     Allow bookings from the past
   * @return {Array}                      Returns a list of booking times with status
   */
  static calulateBookingTimes(
    schedule: IScheduleSubset,
    service: IServiceItemSubset,
    layouts: ILayoutSubset[],
    requestDuration: number,
    requestedPeople: number,
    tables: ITableItem[],
    bookingsByTable: IBookingsByTable,
    preferredSectionId: string,
    requiredTableId: string,
    allowPassTimes: boolean,
    bookingMethod: BookingMethods,
    includeEventBlockedSectionIds = false,
    selectedTime: Date
  ): IBookingInfoAtTime[] {
    if (!bookingsByTable) {
      return [];
    }

    /**
     * @todo: we are relying on momentJS a lot here to create dates (see dateStrToDateTime)
     * Perhaps this is creating a performance issue. Can we try to replace it with something else (like Luxon)?
     * Or perhaps we don't need to do so much Date object conversion and simply keep date strings where possible?
     * Severity: medium
     */

    // compensating for stupid triple 'o' variable name in diary
    const bookingInterval: number =
      service.boookingInterval || schedule.bookingInterval || schedule.boookingInterval;
    const startDate: Date = this.getStartTime(schedule.services[0]);
    const tablesJoins: ITableItem[] = this.getTableJoinsFromLayouts(layouts);

    const bookingTimes: Date[] = this.getBookingTimes(startDate, service, bookingInterval);

    const bookingInfoAtTimeList = bookingTimes.map((time) => {
      const bookingInfoTest: IBookingInfoAtTime = this.getBookingInfoAtTime(
        schedule,
        time,
        startDate,
        service,
        tablesJoins,
        requestDuration,
        requestedPeople,
        tables,
        bookingsByTable,
        preferredSectionId,
        requiredTableId,
        bookingMethod,
        includeEventBlockedSectionIds,
        selectedTime
      );

      // the bug UI-1159 due to table object got changed by line 1170
      // so we should make the freeTablesAtTime and vaildTablesAtTime immutable,
      // whenever table object is changed that will not update freeTablesAtTime and vaildTablesAtTime as well.
      return {
        ...bookingInfoTest,
        freeTablesAtTime: cloneDeep(bookingInfoTest.freeTablesAtTime),
        vaildTablesAtTime: cloneDeep(bookingInfoTest.vaildTablesAtTime)
      }
    })

    return bookingInfoAtTimeList;
  }

  // shallow clone so that tables can be extended inside getBookingInfoAtTime
  static getMutableTables(tables: ITableItem[]): ITableItem[] {
    return tables.map((t) => ({ ...t }));
  }

  // shallow clone so that tables can be extended inside getBookingInfoAtTime
  static getMutableBookingsByTable(bookingsByTable: IBookingsByTable): IBookingsByTable {
    return cloneDeep(bookingsByTable);
  }

  private static getTotalTimes(
    startDate: Date,
    serviceStartDate: Date,
    service: IServiceItemSubsetMinimal,
    bookingInterval: number
  ): number {
    const serviceStartMin: number = DateUtilsService.dateTimeNum(startDate, serviceStartDate);
    const serviceEndMin: number = DateUtilsService.dateTimeNum(startDate, service.endTime);
    return Math.ceil((serviceEndMin - serviceStartMin) / bookingInterval);
  }

  // create a list of possible booking times
  static getBookingTimes(
    startDate: Date,
    service: IServiceItemSubset,
    bookingInterval: number
  ): Date[] {
    const serviceStartDate: Date = service.firstBookingTime || this.getStartTime(service);
    const totalTimes: number = this.getTotalTimes(
      startDate,
      serviceStartDate,
      service,
      bookingInterval
    );

    const bookingTimes: Date[] = [];
    for (let i = 0; i < totalTimes; i++) {
      const time: Date = new Date(serviceStartDate.getTime());
      const minsToAdd: number = i * bookingInterval;
      time.setMinutes(time.getMinutes() + minsToAdd);
      time.setSeconds(0);
      time.setMilliseconds(0);
      bookingTimes.push(time);
    }
    return bookingTimes;
  }

  private static getTableJoinsFromLayouts(layouts: ILayoutSubset[]): ITableItem[] {
    let tablesJoins: ITableItem[] = [];
    layouts.forEach((layout) => {
      if (layout.tablesJoins?.length) {
        let _tablesJoins = layout.tablesJoins;
        if (!Object.isExtensible(_tablesJoins)) {
          // somehow redux is setting layout.tablesJoins and so it can't be extended
          _tablesJoins = cloneDeep(_tablesJoins);
        }
        tablesJoins = tablesJoins.concat(_tablesJoins);
      }
    });

    return tablesJoins;
  }

  static checkBookingsAtTime(
    time: Date,
    bookingDate: Date,
    schedule: IScheduleSubset,
    service: IServiceItemSubset,
    layouts: ILayoutMinimal[],
    requestDuration: number,
    requestedPeople: number,
    tables: ITableItemAbstract[],
    bookingsByTable: IBookingsByTable,
    preferredSectionId: string,
    requiredTableId: string,
    allowPassTimes: boolean,
    bookingMethod: BookingMethods,
    includeEventBlockedSectionIds = false
  ): IBookingInfoAtTime {
    // work out the total time
    const startDate: Date = this.getStartTime(schedule.services[0]);
    const tablesJoins: ITableItem[] = this.getTableJoinsFromLayouts(layouts);

    return this.getBookingInfoAtTime(
      schedule,
      time,
      startDate,
      service,
      tablesJoins,
      requestDuration,
      requestedPeople,
      tables,
      bookingsByTable,
      preferredSectionId,
      requiredTableId,
      bookingMethod,
      includeEventBlockedSectionIds
    );
  }

  // work out the capacity of the restaurant at this time
  private static getMaxCoversAtTime(time: Date, service: IServiceItemSubset): number {
    let maxCovers: number = service.maxCoversPerInterval;

    if (service.enableCustomPaxPerIntervals === true) {
      const formattedTime: string = this.formatDate(time);

      const targetInterval = service.intervalsCustom.find((intv) => intv.times === formattedTime);
      if (targetInterval) {
        if (targetInterval.values || targetInterval.values === 0) {
          maxCovers = targetInterval.values ? (targetInterval.values as number) : 0;
        }
      }
    }

    return maxCovers;
  }

  private static getBlockedSectionAndTableIdsAtTime(
    time: Date,
    blockouts: IBlockout[]
  ): { blockedSectionIds: string[]; blockedTableIds: string[] } {
    let blockedSectionIds: string[] = [];
    let blockedTableIds: string[] = [];
    blockouts.forEach((blockout) => {
      if (DateUtilsService.isInBetweenDates(time, blockout.startDateTime, blockout.endDateTime)) {
        // the time overlaps a blockout
        blockedSectionIds = blockedSectionIds.concat(blockout.blockedSections);
        blockedTableIds = blockedTableIds.concat(blockout.blockedTables.map((table) => table._id));
      }
    });

    return { blockedSectionIds, blockedTableIds };
  }

  private static getBookingIntervalFromService(service: IServiceItemSubset): number {
    // in case the service is an event type, intervalDuration is informed by the property "boookingInterval" (as it is, with misspelling)
    const bookingIntervalAsPossibleString =
      service.type.toLowerCase() === ServiceEventType.Event.toLowerCase()
        ? service.boookingInterval // triple 'o' is a typo we must learn to live with
        : service.intervalsCustom && service.intervalsCustom.length
        ? service.intervalsCustom[0].intervalDuration
        : 0;

    const bookingInterval =
      typeof bookingIntervalAsPossibleString === 'string'
        ? parseInt(bookingIntervalAsPossibleString)
        : bookingIntervalAsPossibleString;

    return bookingInterval;
  }

  private static getBookingInfoAtTime(
    schedule: IScheduleSubset,
    time: Date,
    startDate: Date,
    service: IServiceItemSubset,
    tablesJoins: ITableItem[],
    requestDuration: number,
    requestedPeople: number,
    tables: ITableItemAbstract[],
    bookingsByTable: IBookingsByTable,
    preferredSectionId: string,
    requiredTableId: string,
    bookingMethod: BookingMethods,
    includeEventBlockedSectionIds: boolean,
    selectedTime?: Date
  ): IBookingInfoAtTime {
    let errorMgs: string[] = [];
    let warningMsgs: string[] = [];
    // work out the total time

    // work out the capacity of the restaurant at this time
    // Check what bookings are on at this time
    let coversReachedAtTime = 0;
    let bookingsAtTime: IBookingSubset[] = [];
    let bookingsStartingAtTime: IBookingSubset[] = [];
    // work out which tables are appropriate
    const requestedTime = this.getRequestedTime({
      time,
      startDate,
      service: schedule.services[0],
      requestDuration,
    });

    // work out which tables are appropriate for the selected time
    const selectedRequestedTime = this.getRequestedTime({
      time: selectedTime || time,
      startDate,
      service: schedule.services[0],
      requestDuration,
    });

    startDate = requestedTime.startDate;
    const requestedStartMin = requestedTime.requestedStartMin;
    const requestedEndMin = requestedTime.requestedEndMin;

    let vaildTablesAtTime: ITableItem[] = [];
    let freeTablesAtTime: ITableItem[] = [];
    const invalidTablesAtTime: ITableItem[] = [];
    let tableAvailableLookUp: any = {}; // of ITableAvailableLookUp
    let requiredTable: RequiredTable | null = null;

    const bookingInterval = this.getBookingIntervalFromService(service);

    let bookingSlots = this.getBookingSlotsByServiceSubset({
      service,
      requestedStartMin,
      requestedEndMin,
      bookingInterval,
    });

    tables.forEach((table) => {
      // when this value comes from a redux store it will be immutable, so will need to be cloned if it is to be modified
      if (!Object.isExtensible(table)) {
        console.warn(
          NS,
          `getBookingInfoAtTime`,
          `You are trying to modify the "table" property,
          but it is immutable, probably due to coming from a redux store. Make sure you shallow clone the table objects
          first. Consider calling "getMutableTables" before passing in "tables".`
        );
      }

      table.problemReasons = [];
      const tableId: string = table._id;
      const tableObj = bookingsByTable[tableId];
      tableObj?.bookings.forEach((booking: IBooking) => {
        // when this value comes from a redux store it will be immutable, so will need to be cloned if it is to be modified
        if (!Object.isExtensible(booking)) {
          console.warn(
            NS,
            `getBookingInfoAtTime`,
            `You are trying to modify the "booking" property,
            but it is immutable, probably due to coming from a redux store. Make sure you shallow clone the table objects
            first. Consider calling "getMutableBookingsByTable" before passing in "bookingsByTable".`
          );
        }

        booking.tables = booking.tables.map((bookingTable) =>
          bookingTable._id === tableId ? cloneDeep(table) : bookingTable
        );
      });
      const tableBookings: IBookingSubset[] = tableObj
        ? (tableObj.bookings as IBookingSubset[])
        : [];

      // cleans bookingSlot array if the table type is shared
      if (table.shared) {
        bookingSlots.forEach((item, j) => {
          bookingSlots[j] = 0;
        });
      }

      const setValidTable = this.setValidTable({
        table,
        requestedPeople,
        preferredSectionId,
        tableBookings,
        bookingMethod,
        requiredTableId,
      });

      let available = setValidTable.available;
      let validTable = setValidTable.validTable;

      const bookingGaps = this.getBookingGap({
        requestedStartMin,
        requestedEndMin,
        startDate,
        tableBookings,
        service,
        available,
        bookingsAtTime,
        bookingsStartingAtTime,
        coversReachedAtTime,
        selectedRequestedStartMin: selectedRequestedTime.requestedStartMin, 
        selectedRequestedEndMin: selectedRequestedTime.requestedEndMin
      });

      

      /**
       * What happens here is that this function gets executed for every time and every table. So the passed time was changing every call.
       * this meant that the obstructingBookings was checking not the selected time but every time hence the message was appearing for even non conflicted time
       * We are passing a new variable called selectedTime and then checking the obstructingBookings against that time.
       * 
       */
      // doesnt seem to work if there is a condition - cause the table message get empty array - need to retain the value to show. 
      this.addObstructingBookingMessageToTable(bookingGaps.obstructingBookings, available, table, 
        selectedRequestedTime.requestedStartMin, selectedRequestedTime.requestedEndMin, startDate);

      bookingSlots = this.findBookingSlotsForSharedTables({
        tableBookings,
        requestedTime: time,
        requestedDuration: requestDuration,
        bookingSlots,
      });

      bookingsStartingAtTime = bookingGaps.bookingsStartingAtTime;
      bookingsAtTime = bookingGaps.bookingsAtTime;
      coversReachedAtTime = bookingGaps.coversReachedAtTime;
      available = bookingGaps.available;

      const tableStatus = this.getTableStatus({
        bookingSlots,
        requestedPeople,
        bookingMethod,
        table,
        available,
        validTable,
        vaildTablesAtTime,
        invalidTablesAtTime,
        freeTablesAtTime,
        requiredTableId,
        tableAvailableLookUp,
        includeEventBlockedSectionIds,
        time,
        schedule: schedule,
        serviceId: service._id,
        requestDuration,
      });

      validTable = tableStatus.validTable;
      available = tableStatus.available;
      tableAvailableLookUp = tableStatus.tableAvailableLookUp;
      requiredTable = tableStatus.requiredTable;
      vaildTablesAtTime = tableStatus.vaildTablesAtTime;
      freeTablesAtTime = tableStatus.freeTablesAtTime;
    });

    // work out what joins are free
    const tableJoinsAtTime = this.getTableJoinsAtTime({
      bookingMethod,
      tablesJoins,
      requestedPeople,
      preferredSectionId,
      tableAvailableLookUp,
    });

    const message = this.getMessages({
      tableJoinsAtTime,
      requiredTable,
      requestedPeople,
      coversReachedAtTime,
      service,
      vaildTablesAtTime,
      freeTablesAtTime,
      startDate,
      requestedStartMin,
      requestedEndMin,
      time,
      bookingsStartingAtTime,
    });

    errorMgs = message.errorMgs;
    warningMsgs = message.warningMsgs;
    vaildTablesAtTime = message.vaildTablesAtTime;
    freeTablesAtTime = message.freeTablesAtTime;
    const open = message.open;
    const status = message.status;
    const maxCovers = message.maxCovers;

    return {
      time,
      maxCovers,
      coversReached: coversReachedAtTime,
      bookingsAtTime,
      vaildTablesAtTime,
      freeTablesAtTime,
      vaidJoinsAtTime: tableJoinsAtTime, // typo here, but it is used in many places and we don't want to break it
      status,
      errorMgs,
      warningMsgs,
      open,
      service: service.name,
      invalidTablesAtTime,
    };
  }

  /**
   * Adds bookings to an array (which you must provide) that overlap with requestedStartMin or that
   * prevent a booking to be made, taking into account the default duration of the service.
   * @param bookingStartMin - start time of the booking to check against in minutes
   * @param bookingEndMin - end time of the booking to check against in minutes
   * @param requestedStartMin - selected time to check for existing obstructing bookings
   * @param bookingDuration - default duration for the service to add to the requestedStartMin
   * @param obstructingBookings - the array you want to add to
   * @param booking - the booking that might be obstructing the time
   */
  private static addObstructingBookings(
    bookingStartMin: number,
    bookingEndMin: number,
    requestedStartMin: number,
    requestedEndMin: number,
    obstructingBookings: IBookingSubsetForMessage[],
    booking: IBookingSubsetForMessage
  ): void {
    if (bookingStartMin < requestedEndMin && bookingEndMin > requestedStartMin) {
      /**
       * Note: you can use below for debugging time more easily
       * this.getTimeForDebugging(6, requestedStartMin + service.bookingDuration)
       */

      // make sure the booking is not already in the array
      if (!obstructingBookings.some((b) => b._id) && booking) {
        obstructingBookings.push(booking);
      }
    }
  }

  /**
   * Given a list of bookings that are obstructing the current table, generates a warning message and adds it to the table.
   * @param obstructingBookings - list of bookings that are blocking new bookings from being placed at current time
   * @param available - whether the table is available or not
   * @param table - table to add messages to for the current time
   */
  private static addObstructingBookingMessageToTable(
    obstructingBookings: IBookingSubsetForMessage[],
    available: boolean,
    table: ITableItem,
    requestedStartMin: number,
    requestedEndMin: number,
    startDate: Date
  ): void {
      
    if (!available && obstructingBookings?.length) {
      const messages = obstructingBookings.map((booking) => {
        const bookingEnd: Date = booking.bookingEnd || DateUtilsService.getBookingEnd(booking);
        booking.bookingEnd = bookingEnd;
        const bookingStartMin: number = Math.ceil(
          DateUtilsService.dateTimeNum(startDate, booking.time)
        );
        const bookingEndMin: number = Math.ceil(DateUtilsService.dateTimeNum(startDate, bookingEnd));
        const bookingTime = DateUtilsService.dateToTimeStr12Hour(booking.time);
        if(bookingStartMin < requestedEndMin && bookingEndMin > requestedStartMin) {
          return `Table already booked by ${booking.firstName || ''}${
            booking.lastName ? ' ' + booking.lastName : ''
          } for ${bookingTime}`;
        }
        return null;
      }).filter(Boolean);
      if(messages.length > 0) {
        table.problemReasons.push(...messages);
      }
    }
  }

  /**
   * Give a list of tables and bookings this will map them to bookings by table
   * @param  {Array} bookings
   * @param  {Array} tables
   * @return {Object} an object of tables with bookings array
   */
  static getBookingsByTable(bookings: IBookingSubset[]): IBookingsByTable {
    // work out date
    // group bookings by table
    const bookingsByTable: IBookingsByTable = {};

    bookings.forEach((booking) => {
      if (!booking.status) {
        StatusService.populateMissingStatusInBooking(booking as IBooking);
      }
      if (
        booking.status &&
        booking.status.statusType !== bookingStatusType.cancelled &&
        booking.tables
      ) {
        const tables = booking.tables.filter((table) => table);
        tables.forEach((table) => {
          if (!bookingsByTable[table._id]) {
            bookingsByTable[table._id] = {
              table: table as ITableItem,
              bookings: [],
            };
          }
          bookingsByTable[table._id].bookings.push(booking as IBooking);
        });
      }
    });
    return bookingsByTable;
  }

  // make sure the table is not blocked by an event
  private static getBlockedSectionIdsOnEvents(
    currentServiceId: string,
    time: Date,
    endTime: Date,
    services: IServiceItemSubsetMinimal[]
  ): string[] {
    let result: string[] = [];
    for (let e = services.length - 1; e >= 0; e--) {
      const s = services[e];
      // Check to make sure the service is an event and it does not equal to the current service
      if (s.type && s.type === 'event' && s._id !== currentServiceId) {
        if (endTime.getTime() > s.startTime.getTime() && time.getTime() < s.endTime.getTime()) {
          // the times overlap
          const sectionsIdUsed = s.sectionLayouts.map(({ sectionId }) => sectionId);
          result = result.concat(sectionsIdUsed);
        }
      }
    }

    return result;
  }

  private static getStartTime(
    service: { endTime?: Date; startTime?: Date; times?: IScheduleTime[] },
    useEndTime = false
  ): Date {
    const startDate: Date =
      (useEndTime ? service.endTime : service.startTime) ||
      DateUtilsService.dateStrToDateTime(
        useEndTime ? service.times[service.times.length - 1].time : service.times[0].time
      );
    return startDate;
  }

  private static formatDate(date: Date): string {
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const minutesAsString = minutes < 10 ? '0' + minutes : minutes;
    const strTime = hours + ':' + minutesAsString + ' ' + ampm;
    return strTime;
  }

  private static async checkAvailableTables(
    booking: IBooking,
    schedule: IDiarySchedule,
    serviceIds: string[],
    bookingsByTable: IBookingsByTable,
    timeNow: Date
  ): Promise<IBooking> {
    const serviceIndex = serviceIds.indexOf(booking.serviceId);

    // service may have been deleted, but booking is still in wait list
    if (serviceIndex === -1) {
      booking.tablesAvaiable = false;
      return booking;
    }

    const service = schedule.services[serviceIndex];
    const layoutsIds: string[] = service.sectionLayouts.map(({ layoutId }) => layoutId);
    // get the tables for this service
    const [tablesRes, layouts] = await Promise.all([
      LayoutsService.getByLayouts(layoutsIds, timeNow),
      LayoutsService.getLayouts(layoutsIds, timeNow),
    ]);
    const tables = tablesRes.tablesArray;
    const bookingTime = timeNow > booking.time ? timeNow : booking.time;
    const preferredSectionId = booking.preferredSectionId ? booking.preferredSectionId : 'any';
    const availableTables = this.checkBookingsAtTime(
      bookingTime,
      bookingTime,
      schedule,
      service,
      layouts as unknown as ILayoutMinimal[],
      +booking.duration,
      booking.people,
      cloneDeep(tables),
      bookingsByTable,
      preferredSectionId,
      undefined,
      undefined,
      undefined
    );
    booking.tablesAvaiable = !!(
      availableTables.vaildTablesAtTime.length || availableTables.vaidJoinsAtTime.length
    );
    return booking;
  }

  private static isBookingInWaitList(booking: IBooking): boolean {
    return (
      booking.status &&
      booking.status.statusType !== 'cancelled' &&
      (!booking.tables ||
        booking.tables.length === 0 ||
        booking.bookingType === BookingType.StandbyList)
    );
  }

  static async checkWaitListForAvailableTables(
    schedule: IDiarySchedule,
    bookings: IBooking[],
    bookingsByTable: IBookingsByTable
  ): Promise<IBooking[]> {
    const waitListBookings = bookings.filter((booking: IBooking) =>
      this.isBookingInWaitList(booking)
    );
    if (waitListBookings.length) {
      const serviceIds = schedule.services.map((service) => service._id);
      const timezoneId = AdminService.getVenueTimezoneId();
      const timeNow = DateUtilsService.getVenueTime(timezoneId);
      // There are bookings on the wait list that are ready to be seated.
      await Promise.all(
        waitListBookings.map((booking: IBooking) =>
          this.checkAvailableTables(booking, schedule, serviceIds, bookingsByTable, timeNow)
        )
      );
      return bookings;
    }
    return null;
  }

  static addTablesAvailableFlagToCleanBooking(booking: IBooking, waitListBookings: IBooking[]) : IBooking {
    if (booking.bookingType === BookingType.WaitList) {
      if (!Object.isExtensible(booking)) {
        console.error(NS, 'addTablesAvailableFlagToCleanBooking',
          `The booking object you are trying to modify is immutable (probably from redux store).
          You need to deepClone it first.`);
      }
      const WLBookingWithTablesAvailableFlag = waitListBookings?.find(b => b._id === booking._id);
      if (WLBookingWithTablesAvailableFlag?.tablesAvaiable) {
        booking.tablesAvaiable = true; // spelling is incorrect, but is like this everywhere
      }
    }
    return booking;
  }

  static setSeatNowTime(isDateToday: boolean, timezoneId: string): Date {
    const seatNowTime = DateUtilsService.getVenueTime(timezoneId);
    seatNowTime.setSeconds(0);
    seatNowTime.setMilliseconds(0);
    return seatNowTime;
  }

  static isBookingTimeInList(
    bookingTimes: IBookingInfoAtTime[],
    currentBookingTime: Date
  ): boolean {
    const bookingTimesList = bookingTimes.map((bookingTime) =>
      DateUtilsService.dateToTimeStr(bookingTime.time, 'hh:mm A')
    );
    const bookedTimeStr = DateUtilsService.dateToTimeStr(currentBookingTime, 'hh:mm A');
    return bookingTimesList.indexOf(bookedTimeStr) > -1;
  }

  static getSelectedBookingTimeFromList(
    bookingTimes: IBookingInfoAtTime[],
    currentBookingTime: Date
  ): { selectedTimeId: number | undefined; selectedTime: IBookingInfoAtTime | undefined } {
    let index, item;
    bookingTimes.some((e, i) => {
      if (e.time.getTime() === currentBookingTime.getTime()) {
        index = i;
        item = e;
        return true;
      }
      return false;
    });
    return { selectedTimeId: index, selectedTime: item };
  }

  // Start refactor fn for getBookingInforAtTime
  private static getBookingSlotsByServiceSubset({
    service,
    requestedStartMin,
    requestedEndMin,
    bookingInterval,
  }: {
    service: IServiceItemSubset;
    requestedStartMin: number;
    requestedEndMin: number;
    bookingInterval: number;
  }): number[] {
    const bookingSlots: number[] = [];

    if (requestedEndMin > 0 && bookingInterval > 0) {
      const serviceDuration = service.duration || service.bookingDuration; // could be either (both values mean same thing - duration of the service)
      const reqIntervals = serviceDuration / bookingInterval + 1;

      for (let i = 0; i < reqIntervals; i++) {
        const requestSliceEnd = requestedStartMin + bookingInterval * (i + 1);
        if (requestSliceEnd <= requestedEndMin) {
          bookingSlots.push(0);
        } else {
          break;
        }
      }
    }

    if (!bookingSlots.length) {
      bookingSlots.push(0);
    }

    return bookingSlots;
  }

  private static getTableJoinsAtTime({
    bookingMethod,
    tablesJoins,
    requestedPeople,
    preferredSectionId,
    tableAvailableLookUp,
  }: {
    bookingMethod: BookingMethods;
    tablesJoins: ITableItem[];
    requestedPeople: number;
    preferredSectionId: string;
    tableAvailableLookUp: Record<string, ITableAvailableLookUp>;
  }): ITableItemAbstract[] {
    const joinsAtTime: ITableItemAbstract[] = [];
    let validTable = false;
    if (!Array.isArray(tablesJoins) || tablesJoins.length === 0) {
      return [];
    }

    
    tablesJoins.forEach((tablesJoin) => {
      const isByPhone = bookingMethod === BookingMethods.phone;
      const isByWalkin = bookingMethod === BookingMethods.walkin;
      if (
        (!isByPhone || (!isByWalkin && !tablesJoin.walkinOnly)) &&
        tablesJoin.capacity >= requestedPeople &&
        requestedPeople >= tablesJoin.minCapacity
      ) {
        validTable = true;
      }

      if (validTable) {
        let joinAvailable = true;
        const totalJoins = tablesJoin.tables || [];
        const tablesList: ITableItemAbstract[] = [];
        const tableNames: string[] = [];

        // With every(), return false is equivalent to a break, and return true is equivalent to a continue.
        totalJoins.every((table) => {
          const tableLookUp: ITableAvailableLookUp = tableAvailableLookUp[table];
          if (tableLookUp) {
            tablesList.push(tableLookUp.table);
            tableNames.push(tableLookUp.table.name);
            // if table unavailable and not because it's reserved for walkins, or it's not in a preferred section then the join is unavailable
            if (
              (!tableLookUp.available && !tableLookUp.walkinTable) ||
              (preferredSectionId !== 'any' && preferredSectionId !== tableLookUp.table.sectionId)
            ) {
              // one of the tables are not available
              joinAvailable = false;
              return false;
            }
          }

          return true;
        });

        if (joinAvailable) {

          if (tablesJoin.problemReasons) {
            delete tablesJoin.problemReasons;
          }
          
          /**
           * only care about messages for "too many people" because, 
           * if "not enough people", then the table is not available
           * and if wrong section, then joinAvailable will be false
           */
          const tooManyPeople = requestedPeople > tablesJoin.capacity;
          if (tooManyPeople) {
            tablesJoin.problemReasons = [
              `Too many people on table join (only space for ${tablesJoin.capacity} people)`
            ]
          }

          // note: this may get called 3 or 4 times before getting added
          tablesJoin.tablesList = tablesList;
          tablesJoin.name = tableNames.sort().join('-');
          joinsAtTime.push(tablesJoin);
        }
      }
    });

    return joinsAtTime;
  }

  private static getBookingGap({
    requestedStartMin,
    requestedEndMin,
    startDate,
    tableBookings,
    service,
    available,
    bookingsStartingAtTime,
    bookingsAtTime,
    coversReachedAtTime,
    selectedRequestedStartMin,
    selectedRequestedEndMin,
    
  }: {
    requestedStartMin: number;
    requestedEndMin: number;
    startDate: Date;
    tableBookings: IBookingSubset[];
    service: IServiceItemSubset;
    coversReachedAtTime: number;
    available: boolean;
    bookingsStartingAtTime: IBookingSubset[];
    bookingsAtTime: IBookingSubset[];
    selectedRequestedStartMin: number;
    selectedRequestedEndMin: number;
  }): {
    bookingsStartingAtTime: IBookingSubset[];
    bookingsAtTime: IBookingSubset[];
    obstructingBookings: IBookingSubsetForMessage[];
    coversReachedAtTime: number;
    coversReached: number;
    available: boolean;
  } {
    let coversReached = 0;

    const obstructingBookings: IBookingSubsetForMessage[] = [];

    tableBookings.forEach((booking, t) => {
      const bookingEnd: Date = booking.bookingEnd || DateUtilsService.getBookingEnd(booking);
      booking.bookingEnd = bookingEnd;
      const bookingStartMin: number = Math.ceil(
        DateUtilsService.dateTimeNum(startDate, booking.time)
      );
      const bookingEndMin: number = Math.ceil(DateUtilsService.dateTimeNum(startDate, bookingEnd));

      // if the table is valid check for free space
      // can the request booking fit before this one
      if (t === 0 && requestedStartMin < bookingStartMin && requestedEndMin <= bookingStartMin) {
        available = true;
      }
      // can the request booking fit after this one
      const startGap = bookingEndMin;
      let endGap = null;
      if (t < tableBookings.length - 1) {
        // change the gap end start of the next booking if there is one
        endGap = DateUtilsService.dateTimeNum(startDate, tableBookings[t + 1].time);
      }

      if (requestedStartMin >= startGap) {
        const noGapBetweenEndAndStart = startGap === endGap;
        if (endGap && !noGapBetweenEndAndStart) {
          if (requestedEndMin <= endGap) {
            available = true;
          }
        } else if (endGap === null) {
          available = true;
        }
      }

      let index: number;
      if (bookingStartMin <= requestedStartMin && requestedStartMin < bookingEndMin) {
        // check booking is not a copy
        index = bookingsAtTime
          .map(function (e) {
            return e._id;
          })
          .indexOf(booking._id);
        // make sure the booking is not already in the array
        if (index === -1 && booking) {
          // Added  hot fix for display triggering custom itnervals
          if (booking.serviceId == service._id) {
            coversReached += booking.people;
          }
          bookingsAtTime.push(booking);
        }
      }

      this.addObstructingBookings(
        bookingStartMin,
        bookingEndMin,
        selectedRequestedStartMin,
        selectedRequestedEndMin,
        obstructingBookings,
        booking as unknown as IBookingSubsetForMessage
      );

      if (bookingStartMin === requestedStartMin) {
        // check booking is not a copy
        index = bookingsStartingAtTime
          .map(function (e) {
            return e._id;
          })
          .indexOf(booking._id);
        // make sure the booking is not already in the array
        if (index === -1 && booking) {
          if (booking.serviceId == service._id) {
            coversReachedAtTime += booking.people;
            bookingsStartingAtTime.push(booking);
          }
        }
      } else if (
        booking.serviceId == service._id &&
        service.type.toLowerCase() === ServiceEventType.Event.toLowerCase() &&
        ((bookingStartMin <= requestedStartMin && requestedStartMin < bookingEndMin) ||
          (bookingStartMin < requestedEndMin && requestedEndMin <= bookingEndMin) ||
          (requestedStartMin < bookingStartMin && bookingEndMin < requestedStartMin))
      ) {
        coversReachedAtTime += booking.people;
        bookingsStartingAtTime.push(booking);
      }
    });

    return {
      bookingsStartingAtTime,
      bookingsAtTime,
      obstructingBookings,
      coversReached,
      coversReachedAtTime,
      available,
    };
  }

  // For SharedTable
  public static findBookingSlotsForSharedTables({
    requestedTime,
    requestedDuration,
    tableBookings,
    bookingSlots,
  }: {
    requestedTime: Date;
    requestedDuration: number;
    tableBookings: IBookingSubset[];
    bookingSlots: number[];
  }): number[] {
    const sharedTableBooking = tableBookings.filter((booking) =>
      booking.tables.some((t) => t.shared)
    );

    if (sharedTableBooking.length === 0) return bookingSlots;

    const bookingStartTimeTimeStamps = sharedTableBooking.map((v) => v.time.valueOf());
    const bookingsEndTimeTimeStamps = sharedTableBooking.map((v) => v.bookingEnd.valueOf());

    // Just to ensure the correct order of timestamp which is a big help for booking iteration below
    const booksTime = uniq(
      bookingStartTimeTimeStamps.concat(bookingsEndTimeTimeStamps).sort((a, b) => a - b)
    );
    const requestedEndTime = moment(requestedTime).add(requestedDuration, 'minutes');

    // New Booking
    const startTimeNewRequest = requestedTime.valueOf();
    const endTimeNewRequest = requestedEndTime.valueOf();

    // To prevent undefined value from i + 1, length of iteration should be minus 2
    for (let i = 0; i <= booksTime.length - 2; i++) {
      const firstTimeStamp = booksTime[i];
      const secondTimeStamp = booksTime[i + 1];
      let people = 0;

      // iterate bookings to find how many people booked for this duration (btw firstTimeStamp and secondTimeStamp)
      sharedTableBooking.forEach((v) => {
        const startTimeStamp = v.time.valueOf();
        const endTimeStamp = v.bookingEnd.valueOf();
        const bookedPeople = v.people;

        if (
          (firstTimeStamp <= startTimeStamp && startTimeStamp < secondTimeStamp) ||
          (firstTimeStamp < endTimeStamp && endTimeStamp <= secondTimeStamp) ||
          (startTimeStamp < firstTimeStamp && secondTimeStamp < endTimeStamp)
        ) {
          people += bookedPeople;
        }
      });

      // Only fill up block ( which is covered by new booking ) with people
      // For example: original blocks: [0,0,0,0,0], but only 3rd block, 4th block, 5th block position cover new booking
      if (
        (firstTimeStamp <= startTimeNewRequest && startTimeNewRequest < secondTimeStamp) ||
        (firstTimeStamp < endTimeNewRequest && endTimeNewRequest <= secondTimeStamp) ||
        (startTimeNewRequest < firstTimeStamp && secondTimeStamp < endTimeNewRequest)
      ) {
        bookingSlots.push(people);
      }
    }

    return bookingSlots;
  }

  private static getTableStatus({
    bookingSlots,
    requestedPeople,
    bookingMethod,
    table,
    available,
    validTable,
    vaildTablesAtTime,
    invalidTablesAtTime,
    freeTablesAtTime,
    requiredTableId,
    tableAvailableLookUp,
    includeEventBlockedSectionIds,
    time,
    schedule,
    serviceId,
    requestDuration,
  }: {
    bookingSlots: number[];
    requestedPeople: number;
    bookingMethod: BookingMethods;
    table: ITableItemAbstract;
    available: boolean;
    validTable: boolean;
    vaildTablesAtTime: ITableItem[];
    invalidTablesAtTime: ITableItem[];
    freeTablesAtTime: ITableItem[];
    requiredTableId: string;
    tableAvailableLookUp: Record<string, ITableAvailableLookUp>;
    includeEventBlockedSectionIds: boolean;
    time: Date;
    schedule: IScheduleSubset;
    serviceId: string;
    requestDuration: number;
  }): {
    validTable: boolean;
    available: boolean;
    tableAvailableLookUp: Record<string, ITableAvailableLookUp>;
    requiredTable: RequiredTable | null;
    vaildTablesAtTime: ITableItem[];
    invalidTablesAtTime: ITableItem[];
    freeTablesAtTime: ITableItem[];
  } {
    let blockedTable = false;
    let walkinTable = false;
    let requiredTable: RequiredTable | null = null;

    const endTime: Date = new Date(time.getTime());
    endTime.setMinutes(endTime.getMinutes() + requestDuration);
    const blocked = this.getBlockedSectionAndTableIdsAtTime(time, schedule.blockouts);
    const blockedTableIds = blocked.blockedTableIds;
    const blockedSectionIds = blocked.blockedSectionIds;
    let eventBlockedSectionIds: string[] = [];

    if (includeEventBlockedSectionIds) {
      eventBlockedSectionIds = this.getBlockedSectionIdsOnEvents(
        serviceId,
        time,
        endTime,
        schedule.services
      );
    }

    // Gets the maximum of people that will be booked on any interval
    const maxCoversOnSlots: number =
      bookingSlots && bookingSlots.length > 0 ? Math.max(...bookingSlots) : 0;

    // check if the table is shared and it has enough covers left
    if (table.shared && table.capacity - maxCoversOnSlots >= requestedPeople) {
      available = true;
    } else if (table.shared) {
      validTable = false;
      table.problemReasons.push(
        `Too many people on shared table (only space for ${
          table.capacity - maxCoversOnSlots
        } people)`
      );
    }

    // make sure table is not blocked
    if (
      (available && blockedTableIds.indexOf(table._id) !== -1)
      || (blockedSectionIds.indexOf(table.sectionId) !== -1)
    ) {
      available = false;
      blockedTable = true;
      table.problemReasons.push(`Table has a blockout on it`);
    }

    // Check if it is blocked by events
    if (eventBlockedSectionIds.indexOf(table.sectionId) !== -1) {
      available = false;
      blockedTable = true;
      table.problemReasons.push(`Table is unavailable due to a future event`);
    }

    // make sure the table is not for walk-ins only
    if (available && bookingMethod !== BookingMethods.walkin && table.walkinOnly) {
      available = false;
      walkinTable = true;
      table.problemReasons.push(`Table is set to walk-in only`);
    }

    if (available && validTable && !blockedTable) {
      vaildTablesAtTime.push(table);
    }

    // need to include the invalid tables too, so that blocked out table messaging is still attached to each time
    if (!validTable || blockedTable) {
      invalidTablesAtTime.push(table);
    }

    if (available) {
      freeTablesAtTime.push(table);
    }

    tableAvailableLookUp[table._id] = {
      table: table,
      available: available,
      walkinTable: walkinTable,
    } as ITableAvailableLookUp;

    if (requiredTableId === table._id) {
      requiredTable = ObjectCopyService.angularSafeCopy(table) as RequiredTable;
      requiredTable.validTable = validTable;
      requiredTable.available = available;
    }

    return {
      validTable,
      available,
      tableAvailableLookUp,
      requiredTable,
      vaildTablesAtTime,
      invalidTablesAtTime,
      freeTablesAtTime,
    };
  }

  private static getMessages({
    tableJoinsAtTime,
    requiredTable,
    requestedPeople,
    coversReachedAtTime,
    service,
    vaildTablesAtTime,
    freeTablesAtTime,
    startDate,
    requestedStartMin,
    requestedEndMin,
    time,
    bookingsStartingAtTime,
  }: {
    tableJoinsAtTime: ITableItemAbstract[];
    requiredTable: RequiredTable | null;
    requestedPeople: number;
    coversReachedAtTime: number;
    service: IServiceItemSubset;
    vaildTablesAtTime: ITableItem[];
    freeTablesAtTime: ITableItem[];
    startDate: Date;
    requestedStartMin: number;
    requestedEndMin: number;
    time: Date;
    bookingsStartingAtTime: IBookingSubset[];
  }): {
    errorMgs: string[];
    warningMsgs: string[];
    vaildTablesAtTime: ITableItem[];
    freeTablesAtTime: ITableItem[];
    open: boolean;
    status: TimeStatus;
    maxCovers: number;
  } {
    let errorMgs: string[] = [];
    let warningMsgs: string[] = [];

    const maxCovers: number = this.getMaxCoversAtTime(time, service);
    const serviceStartMin: number = DateUtilsService.dateTimeNum(
      startDate,
      this.getStartTime(service)
    );
    const serviceEndMin: number = DateUtilsService.dateTimeNum(
      startDate,
      this.getStartTime(service, true)
    );
    let status = TimeStatus.available;

    if (vaildTablesAtTime.length === 0 && tableJoinsAtTime.length === 0) {
      errorMgs.push('No tables available.');
    }
    if (errorMgs.length) {
      status = TimeStatus.unavailable;
    }
    if (requiredTable) {
      errorMgs = [];
      warningMsgs = [];
      if (!requiredTable.available) {
        errorMgs.push('T' + requiredTable.name + ' is not available.');
      }
      if (!requiredTable.validTable) {
        if (requestedPeople > requiredTable.capacity) {
          warningMsgs.push(
            'The capacity of T' + requiredTable.name + ' is ' + requiredTable.capacity + '.'
          );
        }
        if (requestedPeople < requiredTable.minCapacity) {
          warningMsgs.push(
            'The min capacity of T' + requiredTable.name + ' is ' + requiredTable.minCapacity + '.'
          );
        }
      }
    }

    if ((maxCovers || maxCovers === 0) && coversReachedAtTime + requestedPeople > maxCovers) {
      warningMsgs.push('You have reached your maximum covers at this time');
    }
    if (requestedEndMin > serviceEndMin) {
      //  warningMsgs.push('This booking ends after ' + service.name + ' has finished.');
    }

    /**
     * In Diary 'lastBookingTime' is present, but on Group Widget (ABC) only `times` is present
     */
    if (!service.lastBookingTime && !service.times) {
      console.warn(
        NS,
        'Could not determine last booking time of service',
        service,
        'So cannot check if booking starts after this.'
      );
    } else {
      const lastBookingTime: Date =
        service.lastBookingTime ||
        DateUtilsService.dateStrToDateTime(service.times[service.times.length - 1].time);
      if (time.getTime() > lastBookingTime.getTime()) {
        warningMsgs.push(
          'This booking starts after your last booking time ' +
            DateUtilsService.dateToTimeStr(lastBookingTime)
        );
        // warningMsgs.push('This booking starts after your last booking time ' + this.$filter('date')(service.lastBookingTime, 'hh:mm a'));
      }
    }

    let open = true;
    if (requestedStartMin < serviceStartMin) {
      open = false;
    } else if (requestedStartMin > serviceEndMin) {
      open = false;
    }
    if (!open) {
      warningMsgs = [];
      vaildTablesAtTime = [];
      freeTablesAtTime = [];
      errorMgs = ['You are closed at this time.'];
    }
    if (
      service.maxBookingsPerInterval &&
      service.maxBookingsPerInterval !== 0 &&
      bookingsStartingAtTime.length >= service.maxBookingsPerInterval
    ) {
      warningMsgs.push('You have reached your maximum bookings at this time');
    }

    return {
      errorMgs,
      warningMsgs,
      vaildTablesAtTime,
      freeTablesAtTime,
      open,
      status,
      maxCovers,
    };
  }

  private static getRequestedTime({
    time,
    startDate,
    service,
    requestDuration,
  }: {
    time: Date;
    startDate: Date;
    service: IServiceItemSubsetMinimal;
    requestDuration: number;
  }): {
    startDate: Date;
    requestedStartMin: number;
    requestedEndMin: number;
  } {
    startDate = this.getStartTime(service);

    const requestedStartMin: number = Math.ceil(DateUtilsService.dateTimeNum(startDate, time));
    const requestedEndMin: number = requestedStartMin + requestDuration;

    return {
      startDate,
      requestedStartMin,
      requestedEndMin,
    };
  }

  private static setValidTable({
    table,
    requestedPeople,
    preferredSectionId,
    tableBookings,
    bookingMethod,
    requiredTableId,
  }: {
    table: ITableItemAbstract;
    requestedPeople: number;
    preferredSectionId: string;
    tableBookings: IBookingSubset[];
    bookingMethod: BookingMethods;
    requiredTableId: string;
  }): {
    available: boolean;
    validTable: boolean;
  } {
    // table is approiate
    let validTable = false;
    let available = false;

    // does the table have the right amout of capacity
    // Is the table in the right section
    const isByPhone = bookingMethod === BookingMethods.phone;
    const isByWalkin = bookingMethod === BookingMethods.walkin;
    if (
      (!isByPhone || (!isByWalkin && !table.walkinOnly)) &&
      (table.capacity >= requestedPeople || table.shared) &&
      requestedPeople >= table.minCapacity &&
      /**
       * If a specific section is selected, this will make tables in other sections orange ("free", but not "valid")/
       */
      (preferredSectionId === SECTION_ANY_ID || preferredSectionId === table.sectionId) &&
      /**
       * @todo: If diary timeline interval is clicked, this will correspond to the table dropdown next to the status bar,
       * which selects the clicked table. That then makes other tables orange in the same section, unless the table dropdown
       * is set to auto. This is a bit confusing and I think should be removed.
       * Severity: medium
       */
      (!requiredTableId || requiredTableId === 'auto' || requiredTableId === table._id)
    ) {
      validTable = true;
    } else {
      if (preferredSectionId !== SECTION_ANY_ID && preferredSectionId !== table.sectionId) {
        table.problemReasons.push(`Table is not in the selected section`);
      }
      if (!table.shared && table.capacity < requestedPeople) {
        table.problemReasons.push(
          `Too many people on table (only space for ${table.capacity} people)`
        );
      }
      if (requestedPeople < table.minCapacity) {
        table.problemReasons.push(`Not enough people on table (minimum is ${table.minCapacity})`);
      }
    }

    // does the table have any bookings at all
    if (!tableBookings.length) {
      available = true;
    }

    return {
      available,
      validTable,
    };
  }
  // End refactor fn for getBookingInforAtTime
}
